import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

import Navbar from "../Component/LandingV2/Navbar";
import { UserInfo } from "../context/AuthContext";
import hashSHA256 from "../utils/hashSHA256";
import "./LoginPage.scss";

export interface LoginPageProps {
  setUserInfo: (userInfo: UserInfo) => void;
}

const LoginPage = (props: LoginPageProps) => {
  // Form state
  const [loadingState, setLoadingState] = useState(false);
  const [messageState, setMessageState] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  // A page (e.g. "View All Ideas") can send the user here to log in and then
  // return to where they came from. Falls back to Home.
  const redirectTo = (location.state as { from?: string } | null)?.from || "/";

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // Dont refresh page
    e.preventDefault();
    // Reset states
    setLoadingState(true);
    setErrorState(null);
    setMessageState(null);

    try {
      // Setting explicit type for e.target to prevent using external libraries
      const eTarget = e.target as typeof e.target & {
        email: { value: string };
        password: { value: string };
      };

      // Destructure values for easier access
      const [email, password] = [eTarget.email.value, eTarget.password.value];
      // console.log(username, email, password, confirmPassword);

      // Send post request to backend
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_URL || "http://localhost:3000"}/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email,
            passwordHash: await hashSHA256(password),
            // passwordHash: password,
            scope: ["default"],
          }),
        }
      );

      const resData = await res.json();
      console.log(resData);
      // If successful, redirect to login page
      if (res.ok) {
        setMessageState(
          redirectTo === "/"
            ? "User verified successfully 🎉 Redirecting to Home"
            : "User verified successfully 🎉 Redirecting…"
        );
        // Set user info
        props.setUserInfo({
          username: resData.username,
          email: resData.email,
          scopes: resData.scopes, //Including scope here..
        });
        // Return the user to where they came from (or Home).
        setTimeout(() => {
          console.log("Redirecting now");
          navigate(redirectTo);
        }, 2000);
      } else {
        // If unsuccessful, display error message
        throw new Error(resData?.message || "Could not verify user");
      }
    } catch (err: any) {
      setErrorState(err?.message || "Could not verify user");
    } finally {
      setLoadingState(false);
    }
  };

  return (
    <div className="login-page-root">
      <Navbar />
      <div className="login-split-page">

        {/* ── LEFT PANEL ── */}
        <div className="login-left-panel">
          {/* <div className="lp-brand">
            <img src="/assets/images/landingv2/main-logo.png" alt="AlgoFabric" className="lp-brand-logo" />
            <span className="lp-brand-name">AlgoFabric</span>
          </div> */}
          <div className="lp-center-text">
            <h2 className="lp-center-heading">
              AlgoFabric: A Financial<br />Insights Companion
            </h2>
            <p className="lp-center-body">
              With the power of NLP, AlgoFabric crafts precision stock reports — the
              perfect financial strategy for your journey.
            </p>
          </div>
          <div className="lp-bottom-spacer" />
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="login-right-panel">
          <div className="login-form-card">
            <h2 className="login-title">Welcome Back</h2>
            <p className="login-subtitle">Sign in to your AlgoFabric account</p>

            <form onSubmit={onSubmit} className="login-form">
              <div className="form-field">
                <label htmlFor="email" className="field-label">Email Address</label>
                <input
                  id="email"
                  className="input-field"
                  type="text"
                  name="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>

              <div className="form-field">
                <label htmlFor="password" className="field-label">Password</label>
                <div className="password-field">
                  <input
                    id="password"
                    className="input-field"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                  <span className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                    <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} className="password-icon" />
                  </span>
                </div>
              </div>

              <div className="forgot-password">
                <Link to="/forgot-password">Forgot Password?</Link>
              </div>

              <button type="submit" className="login-button" disabled={loadingState}>
                {loadingState && (
                  <Icon icon="akar-icons:loading" className="animate-spin" />
                )}
                {loadingState ? "Signing In..." : "Sign In"}
              </button>
            </form>

            {errorState && (
              <p className="feedback-message feedback-message--error">{errorState}</p>
            )}
            {messageState && (
              <p className="feedback-message feedback-message--success">{messageState}</p>
            )}

            <div className="redirect-register">
              Don't have an account?{" "}
              <Link to="/register">Register</Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;
